"use client";

import { NodeIcon } from "@/components/ui/node-icon";
import { useDefinitionByType } from "@/hooks/useActionTriggerDefinitions";
import { useResolvedNodeVisual } from "@/hooks/useResolvedNodeVisual";
import { cn } from "@/lib/utils";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { getRequiredConnectorType } from "@/utils/workflowTransform";
import { EASE_OUT_QUART } from "@/lib/animation-constants";
import { AnimatePresence, motion, useInView } from "motion/react";
import { IconArrowRotateClockwiseOutline18, IconCrosshairsOutline18 } from "nucleo-ui-outline-18";
import { useEffect, useMemo, useRef, useState } from "react";

export type CompactStep = {
  kind: "step";
  id: string;
  number: number;
  name: string;
  type: string;
  isTrigger: boolean;
  connector?: { type: string };
};

export type CompactLoopDivider = {
  kind: "loop-divider";
  label: string;
};

export type CompactLoopBody = {
  kind: "loop-body";
  steps: CompactStep[];
};

export type CompactMore = {
  kind: "more";
  count: number;
};

export type CompactFlowItem = CompactStep | CompactLoopDivider | CompactLoopBody | CompactMore;

export type CompactWorkflowData = {
  triggerSteps: CompactStep[];
  flowItems: CompactFlowItem[];
};

interface CompactWorkflowStepsProps {
  data: CompactWorkflowData;
  className?: string;
  innerClassName?: string;
  /** Enable entrance animations and viewport-triggered reveal */
  animate?: boolean;
  /** Full uncapped list of flow steps — enables page cycling when length > 5 */
  cycleSteps?: CompactStep[];
}

function StepPill({ step }: { step: CompactStep }) {
  const definition = useDefinitionByType(step.type);
  const iconVisual = useResolvedNodeVisual(step.type);
  const connectorTypes = useConnectorsStore(state => state.connectorTypes);
  const requiredConnectorType = getRequiredConnectorType(step.type, definition);
  const connectorTypeDefinition = requiredConnectorType
    ? connectorTypes.find(ct => ct.type === requiredConnectorType)
    : undefined;
  const connectorProp = requiredConnectorType
    ? { type: requiredConnectorType, logoUrl: connectorTypeDefinition?.logoUrl }
    : step.connector?.type
      ? { type: step.connector.type }
      : undefined;

  const iconColor = step.isTrigger ? "#2450FF" : "#22c55e";

  return (
    <div className="bg-ds-surface-muted flex w-full items-center gap-2 rounded-md px-2 py-1">
      <span className="text-ds-text-secondary leading-label w-7 shrink-0 text-center text-sm font-medium tabular-nums">
        {step.number}.
      </span>
      <NodeIcon
        id={step.type}
        subject={step.name}
        context=""
        logoUrl={iconVisual.logoUrl}
        connector={connectorProp ?? iconVisual.connector}
        className="h-6 w-6 shrink-0 rounded-sm"
        alt={step.name}
        color={iconColor}
      />
      <span className="text-ds-text-secondary leading-label min-w-0 flex-1 truncate text-sm font-normal">
        {step.name}
      </span>
    </div>
  );
}

function MoreRow({ count }: { count: number }) {
  return (
    <div className="bg-ds-surface-muted flex w-full items-center gap-2 rounded-md px-2 py-1">
      <span className="text-ds-text-muted leading-label w-7 shrink-0 text-center text-sm font-medium">…</span>
      <span className="text-ds-text-muted leading-label text-sm font-normal">
        +{count} more step{count !== 1 ? "s" : ""}
      </span>
    </div>
  );
}

function StepContainer({
  children,
  variant = "default",
  clip = false,
}: {
  children: React.ReactNode;
  variant?: "default" | "loop";
  clip?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex w-full flex-col items-start justify-center gap-1 rounded-lg border p-1.5",
        variant === "loop" ? "border-amber-500" : "border-ds-border",
        // Cycling: clip exit/enter motion to the bordered container. Soft mask
        // matches inner padding (p-1.5 = 6px) so step pills start/end solid.
        clip &&
          "overflow-hidden [mask-image:linear-gradient(to_bottom,transparent_0,black_6px,black_calc(100%-6px),transparent_100%)]",
      )}
    >
      {children}
    </div>
  );
}

function LoopDividerRow({ label }: { label: string }) {
  return (
    <div className="leading-18 flex items-center justify-center gap-1 py-0.5 text-xs font-medium text-amber-600">
      <IconArrowRotateClockwiseOutline18 className="h-5 w-5" />
      <span>{label}</span>
    </div>
  );
}

function Frame({ headerLabel, children }: { headerLabel?: string; children: React.ReactNode }) {
  return (
    <div className="border-ds-border bg-ds-surface-card relative z-[1] flex w-full flex-col items-start gap-2 rounded-xl border p-3">
      {headerLabel && (
        <div className="text-ds-text-secondary leading-18 flex items-center gap-1 text-xs font-medium">
          <IconCrosshairsOutline18 className="h-5 w-5" />
          <span>{headerLabel}</span>
        </div>
      )}
      {children}
    </div>
  );
}

function FlowFrameBody({ items }: { items: CompactFlowItem[] }) {
  const elements: React.ReactNode[] = [];
  let batch: (CompactStep | CompactMore)[] = [];
  let batchKey = 0;

  const flushBatch = () => {
    if (batch.length === 0) return;
    const snapshot = batch;
    const key = `batch-${batchKey++}`;
    elements.push(
      <StepContainer key={key}>
        {snapshot.map((row, rowIdx) =>
          row.kind === "step" ? (
            <StepPill key={row.id} step={row} />
          ) : (
            <MoreRow key={`more-${key}-${rowIdx}`} count={row.count} />
          ),
        )}
      </StepContainer>,
    );
    batch = [];
  };

  items.forEach((item, idx) => {
    if (item.kind === "step" || item.kind === "more") {
      batch.push(item);
      return;
    }
    flushBatch();
    if (item.kind === "loop-divider") {
      elements.push(<LoopDividerRow key={`divider-${idx}`} label={item.label} />);
    } else if (item.kind === "loop-body") {
      elements.push(
        <StepContainer key={`loop-body-${idx}`} variant="loop">
          {item.steps.map(step => (
            <StepPill key={step.id} step={step} />
          ))}
        </StepContainer>,
      );
    }
  });
  flushBatch();

  return <>{elements}</>;
}

/**
 * Conveyor-belt step cycler: advances one step at a time. The top step exits
 * upward, the remaining steps slide up via layout animation, and a new step
 * enters from below. Window is always WINDOW_SIZE rows so height never shifts.
 */
function CyclingSteps({ allSteps, started }: { allSteps: CompactStep[]; started: boolean }) {
  const WINDOW_SIZE = 5;
  const [offset, setOffset] = useState(0);
  const [cycling, setCycling] = useState(false);
  const hasStartedCycling = useRef(false);

  useEffect(() => {
    if (!started || allSteps.length <= WINDOW_SIZE) return;
    const timer = setTimeout(() => {
      hasStartedCycling.current = true;
      setCycling(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [started, allSteps.length]);

  useEffect(() => {
    if (!cycling) return;
    const interval = setInterval(() => setOffset(prev => prev + 1), 2000);
    return () => clearInterval(interval);
  }, [cycling]);

  const visibleSteps = useMemo(() => {
    const count = Math.min(WINDOW_SIZE, allSteps.length);
    return Array.from({ length: count }, (_, i) => ({
      step: allSteps[(offset + i) % allSteps.length],
      key: offset + i,
    }));
  }, [allSteps, offset]);

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      {visibleSteps.map(({ step, key }) => (
        <motion.div
          key={key}
          layout
          initial={hasStartedCycling.current ? { opacity: 0, y: 24 } : false}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -24 }}
          transition={{
            duration: 0.35,
            ease: EASE_OUT_QUART,
            layout: { duration: 0.3, ease: EASE_OUT_QUART },
          }}
          className="w-full"
        >
          <StepPill step={step} />
        </motion.div>
      ))}
    </AnimatePresence>
  );
}

export function CompactWorkflowSteps({
  data,
  className,
  innerClassName,
  animate = false,
  cycleSteps,
}: CompactWorkflowStepsProps) {
  const { triggerSteps, flowItems } = data;
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.3 });

  if (triggerSteps.length === 0 && flowItems.length === 0) return null;

  const showConnector = triggerSteps.length > 0 && flowItems.length > 0;
  const revealed = !animate || isInView;
  const hasCycling = animate && !!cycleSteps && cycleSteps.length > 5;

  return (
    <div
      ref={containerRef}
      data-compact-workflow-steps
      className={cn(
        "border-ds-border bg-ds-surface-muted relative flex flex-col items-center gap-9 overflow-hidden rounded-xl border px-4 py-[30px]",
        className,
      )}
    >
      <CornerDot className="left-[9.5px] top-[9px]" />
      <CornerDot className="right-[8.7px] top-[9px]" />
      <CornerDot className="bottom-[9px] left-[9.5px]" />
      <CornerDot className="bottom-[9px] right-[9.5px]" />

      <div className={cn("relative z-[1] flex w-full max-w-[406px] flex-col items-stretch", innerClassName)}>
        {triggerSteps.length > 0 && (
          <motion.div
            initial={animate ? { opacity: 0, y: -20 } : false}
            animate={revealed ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
            transition={{ duration: 0.5, ease: EASE_OUT_QUART }}
          >
            <Frame headerLabel="Trigger">
              <StepContainer>
                {triggerSteps.map((step, idx) => (
                  <motion.div
                    key={step.id}
                    initial={animate ? { opacity: 0, x: -16 } : false}
                    animate={revealed ? { opacity: 1, x: 0 } : { opacity: 0, x: -16 }}
                    transition={{
                      duration: 0.35,
                      delay: revealed && animate ? 0.2 + idx * 0.06 : 0,
                      ease: EASE_OUT_QUART,
                    }}
                  >
                    <StepPill step={step} />
                  </motion.div>
                ))}
              </StepContainer>
            </Frame>
          </motion.div>
        )}

        {showConnector && (
          <motion.div
            initial={animate ? { scaleY: 0, opacity: 0 } : false}
            animate={revealed ? { scaleY: 1, opacity: 1 } : { scaleY: 0, opacity: 0 }}
            transition={{ duration: 0.3, delay: revealed && animate ? 0.4 : 0, ease: EASE_OUT_QUART }}
            style={{ transformOrigin: "top" }}
          >
            <DashedConnector />
          </motion.div>
        )}

        {flowItems.length > 0 && (
          <motion.div
            initial={animate ? { opacity: 0, y: 20 } : false}
            animate={revealed ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: revealed && animate ? 0.45 : 0, ease: EASE_OUT_QUART }}
          >
            <Frame>
              {hasCycling ? (
                <StepContainer clip>
                  <CyclingSteps allSteps={cycleSteps} started={revealed} />
                </StepContainer>
              ) : (
                <FlowFrameBody items={flowItems} />
              )}
            </Frame>
          </motion.div>
        )}
      </div>
    </div>
  );
}

/**
 * Vertical dashed line sitting between the trigger and flow frames. Lives in
 * normal flex flow so it provides both the visual line and the vertical gap.
 */
function DashedConnector() {
  return (
    <div className="relative flex h-6 w-full items-center justify-center">
      <div className="border-ds-neutral-300 h-full w-0 border-l-2 border-dashed" />
    </div>
  );
}

function CornerDot({ className }: { className?: string }) {
  return <div className={cn("bg-ds-neutral-900/15 absolute h-2 w-2 rounded-full", className)} />;
}

function formatNodeId(nodeId: string): string {
  return nodeId
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

type BuilderNode = {
  type: string;
  children?: string[];
  loopBody?: string[];
  connector?: { type: string } | null;
  titleOverride?: string;
};

type BuilderDefinition = {
  nodes?: Record<string, BuilderNode>;
};

type CompactDefinitionLite = { type: string; name?: string };

interface BuildCompactOptions {
  /** Max number of step rows (regular + loop-body) rendered in the flow area. Default 5. */
  maxFlowSteps?: number;
  /** When true (default), append a "+N more steps" row after clipping. */
  showOverflowRow?: boolean;
}

export function buildCompactWorkflowData(
  definition: BuilderDefinition | null | undefined,
  triggers: CompactDefinitionLite[],
  actions: CompactDefinitionLite[],
  options?: BuildCompactOptions,
): CompactWorkflowData {
  const maxFlowSteps = options?.maxFlowSteps ?? 5;
  const showOverflowRow = options?.showOverflowRow ?? true;

  const empty: CompactWorkflowData = { triggerSteps: [], flowItems: [] };
  const nodesObj = definition?.nodes;
  if (!nodesObj) return empty;

  const keys = Object.keys(nodesObj);
  if (keys.length === 0) return empty;

  const triggerByType = new Map(triggers.map(t => [t.type, t]));
  const actionByType = new Map(actions.map(a => [a.type, a]));

  const childrenSet = new Set<string>();
  Object.values(nodesObj).forEach(node => {
    node.children?.forEach(id => childrenSet.add(id));
    node.loopBody?.forEach(id => childrenSet.add(id));
  });
  const rootNodeIds = keys.filter(id => !childrenSet.has(id));
  if (rootNodeIds.length === 0) rootNodeIds.push(keys[0]);

  let counter = 0;
  const toStep = (nodeId: string, node: BuilderNode): CompactStep => {
    counter++;
    const isTrigger = triggerByType.has(node.type);
    const definition = isTrigger ? triggerByType.get(node.type) : actionByType.get(node.type);
    return {
      kind: "step",
      id: nodeId,
      number: counter,
      name: node.titleOverride || definition?.name || formatNodeId(nodeId),
      type: node.type,
      isTrigger,
      connector: node.connector || undefined,
    };
  };

  const triggerSteps: CompactStep[] = [];
  const rawFlowItems: CompactFlowItem[] = [];
  const visited = new Set<string>();

  const visit = (nodeId: string) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    const node = nodesObj[nodeId];
    if (!node) return;
    const step = toStep(nodeId, node);
    if (step.isTrigger) {
      triggerSteps.push(step);
    } else {
      rawFlowItems.push(step);
    }
    if (node.type === "for-each") {
      const loopBodySteps: CompactStep[] = [];
      node.loopBody?.forEach(bodyId => {
        if (visited.has(bodyId)) return;
        visited.add(bodyId);
        const bodyNode = nodesObj[bodyId];
        if (!bodyNode) return;
        loopBodySteps.push(toStep(bodyId, bodyNode));
      });
      if (loopBodySteps.length > 0) {
        rawFlowItems.push({ kind: "loop-divider", label: "Loop" });
        rawFlowItems.push({ kind: "loop-body", steps: loopBodySteps });
      }
    }
    node.children?.forEach(childId => visit(childId));
  };
  rootNodeIds.forEach(id => visit(id));

  let remaining = maxFlowSteps;
  let overflow = 0;
  const cappedFlowItems: CompactFlowItem[] = [];
  for (let i = 0; i < rawFlowItems.length; i++) {
    const item = rawFlowItems[i];
    if (item.kind === "step") {
      if (remaining > 0) {
        cappedFlowItems.push(item);
        remaining--;
      } else {
        overflow++;
      }
    } else if (item.kind === "loop-body") {
      if (remaining > 0) {
        const kept = item.steps.slice(0, remaining);
        cappedFlowItems.push({ kind: "loop-body", steps: kept });
        remaining -= kept.length;
        overflow += item.steps.length - kept.length;
      } else {
        overflow += item.steps.length;
      }
    } else if (item.kind === "loop-divider") {
      // Only keep the divider when the following loop-body has at least one kept step.
      const nextItem = rawFlowItems[i + 1];
      if (remaining > 0 && nextItem && nextItem.kind === "loop-body" && nextItem.steps.length > 0) {
        cappedFlowItems.push(item);
      }
    } else if (item.kind === "more") {
      cappedFlowItems.push(item);
    }
  }
  if (overflow > 0 && showOverflowRow) {
    cappedFlowItems.push({ kind: "more", count: overflow });
  }

  return { triggerSteps, flowItems: cappedFlowItems };
}
